import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { map } from 'rxjs';

import {
  BUSINESS_DOCUMENT_DELETE_PIPELINE_ENDPOINT,
  BUSINESS_DOCUMENT_CREATE_PIPELINE_ENDPOINT,
  BUSINESS_DOCUMENT_ENDPOINTS,
  BUSINESS_DOCUMENT_GET_BUSINESS_DOCUMENT_DEFINITIONS_ENDPOINT,
  BUSINESS_DOCUMENT_GET_DOCUMENTS_ENDPOINT,
  BUSINESS_DOCUMENT_GET_DOCUMENTS_SEARCH_SCHEMA_ENDPOINT,
  BUSINESS_DOCUMENT_GET_DRIVE_ENDPOINT,
  BUSINESS_DOCUMENT_GET_PIPELINES_ENDPOINT,
  BUSINESS_DOCUMENT_PIPELINE_ENDPOINT,
  BUSINESS_DOCUMENT_POST_PIPELINE_DUPLICATE_ENDPOINTS,
  BUSINESS_DOCUMENT_POST_PIPELINE_MAKE_DEFAULT_ENDPOINT,
  BUSINESS_DOCUMENT_POST_COMPANY_FOLDER_ENDPOINT,
  PERMISSIONS_GET_ALLOWED_BRANCHES_ENDPOINT,
} from '../api/api-endpoints';
import { RestApiService } from '../api/rest-api.service';
import { buildSearchQueryString } from '../utils/search-query-params.utils';
import { formatString } from '../utils/string.utils';
import {
  SearchFilterSchema,
  SearchListRequestPayload,
} from '../../shared/search-filter-pagination/search-bar/search-bar.types';

export interface BusinessDocumentListItem {
  id: string;
  branch_id: string;
  document_number: string;
  display_name: string;
  reference: string;
  owner_type: string;
  document_type_key: string;
  branch_name?: string | null;
  stage: string;
  status: string;
  pipeline_step_name?: string | null;
  pipeline_step_color?: string | null;
  directory_path?: string | null;
  document_date?: string | null;
  amount_total: string | number;
  currency_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface BusinessDocumentDrive {
  documents: BusinessDocumentListItem[];
  document_types: Array<{
    key: string;
    owner: string;
    name: string;
  }>;
  branches: Array<{
    id: string;
    name: string;
  }>;
  company_folders: BusinessDocumentCompanyFolder[];
}

export interface BusinessDocumentCompanyFolder {
  id: string;
  name: string;
  created_at: string;
}

export interface BusinessDocumentListPage {
  data: BusinessDocumentListItem[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
}

export interface BusinessDocumentDefinition {
  key: string;
  owner: string;
  name: string;
  description: string;
  permission_resource: string;
  default_pipeline: {
    code: string;
    name: string;
    prefix: string;
    editable: boolean;
    allow_step_skipping: boolean;
    allow_non_initial_start: boolean;
    steps: BusinessDocumentSchemaStep[];
    transitions: Array<{
      from: string | null;
      to: string;
      action: string;
      label: string;
      style: string;
      requires_verification: boolean;
    }>;
  } | null;
  pipeline_templates: Array<{
    code: string;
    name: string;
    prefix: string;
    editable: boolean;
    allow_step_skipping: boolean;
    allow_non_initial_start: boolean;
    steps: BusinessDocumentSchemaStep[];
    transitions: Array<{
      from: string | null;
      to: string;
      action: string;
      label: string;
      style: string;
      requires_verification: boolean;
    }>;
  }>;
  contributor_roles: ContributorRole[];
  journal_defaults: {
    journal_setting_key: string;
    debit_account_setting_key: string;
    credit_account_setting_key: string;
    tax_setting_key: string | null;
  } | null;
  currency_required: boolean;
  partner_role: 'customer' | 'vendor' | null;
}

export interface BusinessDocumentSchemaStep {
  code: string;
  name: string;
  stage: string;
  status: string;
  color: string;
  initial: boolean;
  required: boolean;
  terminal: boolean;
  editable: boolean;
  immutable: boolean;
  verification_enabled: boolean;
  verification_sequential: boolean;
  owner_contributor_role_code: string;
  post_actions: Array<{
    key: string;
    execution_side: string;
    activation: 'before_transition' | 'action_button';
    mandatory: boolean;
  }>;
}

export interface ContributorRole {
  code: string;
  name: string;
  required: boolean;
  can_view: boolean;
  can_edit: boolean;
  can_transition: boolean;
  can_verify: boolean;
  policies: Array<{ resource: string; actions: string[] }>;
  step_codes: string[];
}

export interface AllowedBranch {
  id: string;
  value: string;
  label: string;
  code: string | null;
  is_default: boolean;
  operating_mode: 'independent' | 'dependent';
}

export interface AllowedBranchesResponse {
  resource: string;
  action: string;
  branches: AllowedBranch[];
  can_access_all_branches: boolean;
  can_access_own_data: boolean;
  employment_id: string | null;
}

export interface BusinessDocumentNextStepUpdate<TPayload> {
  steps: {
    pipeline_id: string;
    current_step_id: string;
  };
  next_step_clicked: string;
  business_document_id: string;
  payload: TPayload;
}

export interface BusinessDocumentPipeline {
  id: string;
  organisation_id: string;
  branch_id: string;
  document_type_key: string;
  code: string;
  name: string;
  description: string | null;
  prefix: string;
  next_number: number;
  is_default: boolean;
  is_editable: boolean;
  allow_step_skipping: boolean;
  allow_non_initial_start: boolean;
  active: boolean;
  applies_all_branches: boolean;
  created_invoice_pipeline_id: string | null;
  created_delivery_pipeline_id: string | null;
}

export interface BusinessDocumentContributor {
  id: string;
  contributor_role_code: string;
  contributor_role_name: string;
  subject_type: 'employee' | 'team' | 'role' | 'branch' | 'department' | 'employee_group';
  subject_id: string;
  grants_access: boolean;
  active: boolean;
}

export type BusinessDocumentStepType =
  | 'in_step'
  | 'out_step'
  | 'contract_sign'
  | 'listener'
  | 'inventory_movement'
  | 'success'
  | 'cancelled';

export interface InventoryMovement { from: string; to: string; movement_type: 'internal' | 'transit'; }
export type InventoryRemovalMethod = 'fifo' | 'lifo' | 'fefo' | 'closest_location' | 'least_packages';
export interface InventoryPutawayRule {
  id: string; name: string; sequence: number; product_id: string | null; product_category_id: string | null;
  package_type_ids: string[]; source_location_id: string; destination_location_id: string;
  sublocation_mode: 'no' | 'last_used' | 'closest_location'; storage_category_id: string | null;
  legacy_provenance?: unknown;
}
export interface InventoryPutawayConfiguration { default_location_id: string | null; inherit_destination_policy?: boolean; rules: InventoryPutawayRule[]; }
export interface InventoryRemovalConfiguration {
  source_location_id: string; strategy: InventoryRemovalMethod;
  category_overrides: Array<{product_category_id: string; strategy: InventoryRemovalMethod}>;
  location_overrides: Array<{location_id: string; strategy: InventoryRemovalMethod}>;
  legacy_provenance?: unknown;
}

export interface BusinessDocumentStep {
  id: string;
  code: string;
  name: string;
  stage: string;
  status: string;
  description: string | null;
  sequence: number;
  color: string | null;
  is_initial: boolean;
  is_middle: boolean;
  create_button_label: string | null;
  step_type: BusinessDocumentStepType;
  listener_key: string | null;
  is_out_step: boolean;
  out_step_template: string | null;
  out_step_message: string | null;
  out_step_link_expiry_hours: number;
  is_required: boolean;
  is_terminal: boolean;
  allows_document_edit: boolean;
  verification_enabled: boolean;
  verification_sequential: boolean;
  is_immutable: boolean;
  active: boolean;
  metadata: Record<string, unknown>;
}

export interface BusinessDocumentPipelineBundle {
  pipeline: BusinessDocumentPipeline;
  steps: Array<{
    step: BusinessDocumentStep;
    actions: Array<{
      id: string;
      action_key: string;
      execution_side: string;
      is_mandatory: boolean;
      enabled: boolean;
      sequence: number;
      configuration: Record<string, unknown>;
    }>;
    verification_groups: Array<{
      group: {
        id: string;
        name: string;
        sequence: number;
        required_approvals: number;
        active: boolean;
      };
      contributor_ids: string[];
    }>;
  }>;
  transitions: Array<{
    id: string;
    from_step_id: string | null;
    to_step_id: string;
    action_code: string;
    button_label: string;
    button_style: string;
    sequence: number;
    requires_verification: boolean;
    is_default: boolean;
    active: boolean;
  }>;
  contributors: BusinessDocumentContributor[];
  branch_ids: string[];
  validation_problems: string[];
  validation_issues?: Array<{
    code: string;
    severity: 'error' | 'warning';
    message: string;
    step_id?: string | null;
    step_code?: string | null;
    step_name?: string | null;
    contributor_role_code?: string | null;
    contributor_role_name?: string | null;
    remediation?: string | null;
  }>;
  is_valid: boolean;
}

export interface BusinessDocumentWorkspaceModel {
  document: {
    id: string;
    owner_type: string;
    owner_record_id: string;
    document_kind: string;
    document_number: string;
    display_name: string;
    reference: string;
    document_type_key: string;
    branch_id: string;
    stage: string;
    status: string;
    amount_total: string | number;
    currency_id: string | null;
    current_step_id: string | null;
    partner_id: string | null;
  };
  pipeline: BusinessDocumentPipelineBundle;
  available_transitions: BusinessDocumentPipelineBundle['transitions'];
  timeline: Array<Record<string, unknown>>;
  verification_requests: Array<Record<string, unknown>>;
  verification_notes: Array<Record<string, unknown>>;
  available_contributor_roles: string[];
  communication_contributors: Array<{
    employment_id: string;
    user_id: string;
    display_name: string;
    email: string;
    role_name: string;
  }>;
  active_step_owner_contributor_id: string | null;
  can_edit_current_step: boolean;
  is_admin: boolean;
  out_step_link?: OutStepLink | null;
}

export interface OutStepLink {
  url: string;
  expires_at: string;
}

export interface PublicOutStepAction {
  code: string;
  label: string;
  style: string;
  decision: 'accept' | 'decline';
}

export interface PublicOutStep {
  document: {
    document_number: string;
    display_name: string;
    document_type_key: string;
    stage: string;
    status: string;
    document_date: string | null;
    amount_total: string | number;
    currency_id: string | null;
  };
  template: string;
  message: string | null;
  snapshot: Record<string, unknown>;
  actions: PublicOutStepAction[];
  expires_at: string;
}

export interface BusinessDocumentStepAccess {
  business_document_id: string;
  pipeline_id: string;
  step_id: string | null;
  allowed_view_types: Array<'observe' | 'edit'>;
  allowed_contributors: Array<{
    contributor_id: string;
    role_code: string;
    role_name: string;
    is_step_editor: boolean;
  }>;
  editing_contributor: {
    contributor_id: string;
    role_code: string;
    role_name: string;
    is_step_editor: true;
  } | null;
  is_admin: boolean;
}

export interface VerificationRequest {
  id: string;
  business_document_id: string;
  document_number: string;
  display_name: string;
  document_type_key: string;
  branch_id: string;
  step_name: string;
  group_name: string;
  status: string;
  required_approvals: number;
  received_approvals: number;
  has_decided: boolean;
  started_at: string;
}

export interface ConfigurePipelineInput {
  name: string;
  description: string | null;
  active: boolean;
  allow_step_skipping: boolean;
  allow_non_initial_start: boolean;
  applies_all_branches: boolean;
  created_invoice_pipeline_id: string | null;
  created_delivery_pipeline_id: string | null;
  branch_ids: string[];
  steps: Array<{
    id: string | null;
    code: string;
    name: string;
    stage: string;
    status: string;
    description: string | null;
    sequence: number;
    color: string | null;
    is_initial: boolean;
    is_middle: boolean;
    create_button_label: string | null;
    step_type: BusinessDocumentStepType;
    inventory_movement: InventoryMovement | null;
    putaway_configuration: InventoryPutawayConfiguration | null;
    removal_configuration: InventoryRemovalConfiguration | null;
    listener_key: string | null;
    is_out_step: boolean;
    out_step_template: string | null;
    out_step_message: string | null;
    out_step_link_expiry_hours: number;
    is_contract_step: boolean;
    contract_template_mode: string;
    contract_template_id: string | null;
    contract_template_name: string | null;
    contract_signing_deadline_hours: number;
    is_required: boolean;
    is_terminal: boolean;
    allows_document_edit: boolean;
    verification_enabled: boolean;
    verification_sequential: boolean;
    active: boolean;
    owner_contributor_id: string | null;
    canvas_x: number | null;
    canvas_y: number | null;
    connection_bends: Record<string, { x: number; y: number }>;
    action_buttons: BusinessDocumentStepActionButton[];
    action_buttons_explicit: boolean;
    post_actions: Array<{
      action_key: string;
      execution_side: string;
      is_mandatory: boolean;
      enabled: boolean;
      sequence: number;
      configuration: Record<string, unknown>;
    }>;
    verification_groups: Array<{
      id: string | null;
      name: string;
      sequence: number;
      required_approvals: number;
      active: boolean;
      contributor_ids: string[];
    }>;
  }>;
  transitions: Array<{
    id: string | null;
    from_step_code: string | null;
    to_step_code: string;
    action_code: string;
    button_label: string;
    button_style: string;
    sequence: number;
    requires_verification: boolean;
    is_default: boolean;
    active: boolean;
  }>;
  contributors: Array<{
    id: string | null;
    contributor_role_code: string;
    contributor_role_name: string;
    subject_type: string;
    subject_id: string;
    grants_access: boolean;
    active: boolean;
  }>;
}

export interface CreatePipelineInput {
  document_type_key: string;
  schema_code: string | null;
  branch_ids: string[];
  applies_all_branches: boolean;
  name: string;
  contributor_employee_group_ids: Record<string, string[]>;
}

export interface PipelineApiError {
  code: string;
  message: string;
  description: string | null;
}

export function pipelineApiError(error: unknown, fallback: string): PipelineApiError {
  const payload = (error as { error?: Partial<PipelineApiError> } | null)?.error;
  return {
    code: typeof payload?.code === 'string' ? payload.code : 'PIPELINE_REQUEST_FAILED',
    message: typeof payload?.message === 'string' ? payload.message : fallback,
    description: typeof payload?.description === 'string' ? payload.description : null,
  };
}

export interface ContributorOption {
  value: string;
  label: string;
  code: string | null;
}

export interface BusinessDocumentStepActionButton {
  code: string;
  label: string;
  style: string;
  sequence: number;
  active: boolean;
  multiple_steps?: boolean;
}

export interface BusinessDocumentActionButtonDefinition extends BusinessDocumentStepActionButton {
  multiple_steps: boolean;
}

/**
 * Code-owned owner commands exposed by each business-document UI. Pipeline
 * configuration can place these commands, but cannot invent commands that the
 * owning page does not implement.
 */
export function businessDocumentActionButtons(
  documentTypeKey: string,
): BusinessDocumentActionButtonDefinition[] {
  const reusable = (code: string, label: string, style = 'secondary'): BusinessDocumentActionButtonDefinition => ({
    code, label, style, sequence: 10, active: true, multiple_steps: true,
  });
  const single = (code: string, label: string, style = 'primary'): BusinessDocumentActionButtonDefinition => ({
    code, label, style, sequence: 10, active: true, multiple_steps: false,
  });

  if (documentTypeKey === 'sales.order') {
    return [
      reusable('print', 'Print'),
      single('unlock', 'Unlock'),
      single('create_invoice', 'Create invoice'),
      reusable('cancel', 'Cancel', 'danger'),
    ];
  }
  if (documentTypeKey === 'procurement.purchase') {
    return [
      single('send', 'Send RFQ'),
      single('convert', 'Convert to purchase order'),
      single('confirm', 'Confirm order'),
      single('unlock', 'Unlock'),
      single('receive', 'Receive products'),
      single('create_vendor_bill', 'Create vendor bill'),
      reusable('cancel', 'Cancel', 'danger'),
    ];
  }
  if (documentTypeKey.startsWith('finance.transaction.')) {
    return [single('pay_now', 'Register payment')];
  }
  if (documentTypeKey.startsWith('inventory.stock-transfer.')) {
    return [
      single('check_availability', 'Check availability'),
      single('reserve', 'Reserve'),
      single('validate', 'Validate', 'success'),
      reusable('cancel', 'Cancel', 'danger'),
    ];
  }
  if (documentTypeKey.startsWith('crm.prospect.') || documentTypeKey.startsWith('helpdesk.')) {
    return [reusable('archive', 'Archive', 'danger')];
  }
  return [];
}

export function configurePipelineInput(
  bundle: BusinessDocumentPipelineBundle,
): ConfigurePipelineInput {
  const stepCodeById = new Map(
    bundle.steps
      .filter((item) => item.step.id)
      .map((item) => [item.step.id, item.step.code]),
  );
  return {
    name: bundle.pipeline.name.trim(),
    description: bundle.pipeline.description?.trim() || null,
    active: bundle.pipeline.active,
    allow_step_skipping: bundle.pipeline.allow_step_skipping,
    allow_non_initial_start: bundle.pipeline.allow_non_initial_start,
    applies_all_branches: bundle.pipeline.applies_all_branches,
    created_invoice_pipeline_id: persistedUuid(bundle.pipeline.created_invoice_pipeline_id),
    created_delivery_pipeline_id: persistedUuid(bundle.pipeline.created_delivery_pipeline_id),
    branch_ids: bundle.branch_ids,
    steps: bundle.steps.map((item) => ({
      id: persistedUuid(item.step.id),
      code: item.step.code.trim(),
      name: item.step.name.trim(),
      stage: item.step.stage.trim(),
      status: item.step.status.trim(),
      description: item.step.description?.trim() || null,
      sequence: item.step.sequence,
      color: item.step.color,
      is_initial: item.step.is_initial,
      is_middle: item.step.is_middle,
      create_button_label: item.step.create_button_label?.trim() || null,
      step_type: item.step.step_type ?? (item.step.is_out_step ? 'out_step' : 'in_step'),
      inventory_movement: item.step.step_type === 'inventory_movement' ? (item.step.metadata?.['inventory_movement'] as InventoryMovement ?? null) : null,
      putaway_configuration: item.step.step_type === 'inventory_movement' ? (item.step.metadata?.['putaway_configuration'] as InventoryPutawayConfiguration ?? null) : null,
      removal_configuration: item.step.step_type === 'inventory_movement' ? (item.step.metadata?.['removal_configuration'] as InventoryRemovalConfiguration ?? null) : null,
      listener_key: item.step.step_type === 'listener' ? item.step.listener_key?.trim() || null : null,
      is_out_step: item.step.step_type === 'out_step',
      out_step_template: item.step.step_type === 'out_step' ? item.step.out_step_template?.trim() || 'default' : null,
      out_step_message: item.step.step_type === 'out_step' ? item.step.out_step_message?.trim() || null : null,
      out_step_link_expiry_hours: item.step.step_type === 'out_step' ? item.step.out_step_link_expiry_hours : 168,
      is_contract_step: item.step.step_type === 'contract_sign',
      contract_template_mode: String(item.step.metadata?.['contract_template_mode'] ?? 'default'),
      contract_template_id: typeof item.step.metadata?.['contract_template_id'] === 'string'
        ? persistedUuid(item.step.metadata['contract_template_id'] as string)
        : null,
      contract_template_name: typeof item.step.metadata?.['contract_template_name'] === 'string'
        ? String(item.step.metadata['contract_template_name'])
        : null,
      contract_signing_deadline_hours: Number(item.step.metadata?.['contract_signing_deadline_hours'] ?? 168),
      is_required: item.step.is_required,
      is_terminal: item.step.is_terminal,
      allows_document_edit: item.step.allows_document_edit,
      verification_enabled: item.step.verification_enabled,
      verification_sequential: item.step.verification_sequential,
      active: item.step.active,
      owner_contributor_id: typeof item.step.metadata?.['owner_contributor_id'] === 'string'
        ? persistedUuid(item.step.metadata['owner_contributor_id'] as string)
        : null,
      canvas_x: canvasCoordinate(item.step.metadata, 'x'),
      canvas_y: canvasCoordinate(item.step.metadata, 'y'),
      connection_bends: connectionBends(item.step.metadata),
      action_buttons: Array.isArray(item.step.metadata?.['action_buttons'])
        ? (item.step.metadata['action_buttons'] as BusinessDocumentStepActionButton[])
        : [],
      action_buttons_explicit: item.step.metadata?.['action_buttons_explicit'] === true,
      post_actions: item.actions.map((action) => ({
        action_key: action.action_key,
        execution_side: action.execution_side,
        is_mandatory: action.is_mandatory,
        enabled: action.is_mandatory || action.enabled,
        sequence: action.sequence,
        configuration: action.configuration,
      })),
      verification_groups: item.verification_groups.map((group) => ({
        id: group.group.id || null,
        name: group.group.name.trim(),
        sequence: group.group.sequence,
        required_approvals: group.group.required_approvals,
        active: group.group.active,
        contributor_ids: group.contributor_ids,
      })),
    })),
    transitions: bundle.transitions.map((transition) => {
      const toStep =
        bundle.steps.find((item) => item.step.id === transition.to_step_id) ??
        bundle.steps.find((item) => item.step.code === transition.to_step_id);
      const fromCode = transition.from_step_id
        ? stepCodeById.get(transition.from_step_id) ?? transition.from_step_id
        : null;
      return {
        id: persistedUuid(transition.id),
        from_step_code: fromCode,
        to_step_code: toStep?.step.code ?? transition.to_step_id,
        action_code: transition.action_code.trim(),
        button_label: transition.button_label.trim(),
        button_style: transition.button_style,
        sequence: transition.sequence,
        requires_verification: transition.requires_verification,
        is_default: transition.is_default,
        active: transition.active,
      };
    }),
    contributors: bundle.contributors.map((contributor) => ({
      id: persistedUuid(contributor.id),
      contributor_role_code: contributor.contributor_role_code,
      contributor_role_name: contributor.contributor_role_name,
      subject_type: contributor.subject_type,
      subject_id: contributor.subject_id,
      grants_access: contributor.grants_access,
      active: contributor.active,
    })),
  };
}

function persistedUuid(value: string | null | undefined): string | null {
  return value && !value.startsWith('local:') ? value : null;
}

function canvasCoordinate(metadata: Record<string, unknown>, axis: 'x' | 'y'): number | null {
  const canvas = metadata?.['canvas'];
  if (!canvas || typeof canvas !== 'object') return null;
  const value = (canvas as Record<string, unknown>)[axis];
  return typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : null;
}

function connectionBends(
  metadata: Record<string, unknown>,
): Record<string, { x: number; y: number }> {
  const source = metadata?.['connection_bends'];
  if (!source || typeof source !== 'object' || Array.isArray(source)) return {};
  const result: Record<string, { x: number; y: number }> = {};
  for (const [key, value] of Object.entries(source as Record<string, unknown>)) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const point = value as Record<string, unknown>;
    if (typeof point['x'] === 'number' && typeof point['y'] === 'number'
      && Number.isFinite(point['x']) && Number.isFinite(point['y'])) {
      result[key] = { x: Math.round(point['x']), y: Math.round(point['y']) };
    }
  }
  return result;
}

@Injectable({
  providedIn: 'root',
})
export class BusinessDocument {
  constructor(private readonly api: RestApiService) {}

  definitions() {
    return this.api.get<BusinessDocumentDefinition[]>(BUSINESS_DOCUMENT_GET_BUSINESS_DOCUMENT_DEFINITIONS_ENDPOINT);
  }

  documentSearchSchema() {
    return this.api.get<SearchFilterSchema>(BUSINESS_DOCUMENT_GET_DOCUMENTS_SEARCH_SCHEMA_ENDPOINT);
  }

  listDocuments(payload: Partial<SearchListRequestPayload>) {
    return this.api.get<BusinessDocumentListPage>(
      `${BUSINESS_DOCUMENT_GET_DOCUMENTS_ENDPOINT}${buildSearchQueryString(payload)}`,
    );
  }

  drive() {
    return this.api.get<BusinessDocumentDrive>(BUSINESS_DOCUMENT_GET_DRIVE_ENDPOINT);
  }

  createCompanyFolder(name: string) {
    return this.api.post<BusinessDocumentCompanyFolder>(
      BUSINESS_DOCUMENT_POST_COMPANY_FOLDER_ENDPOINT,
      { name },
    );
  }

  allowedBranches(resource: string, action: string) {
    const params = new HttpParams().set('resource', resource).set('action', action);
    return this.api.get<AllowedBranchesResponse>(PERMISSIONS_GET_ALLOWED_BRANCHES_ENDPOINT, { params });
  }

  pipelines(documentTypeKey: string, branchId?: string, stepCount?: number) {
    let params = new HttpParams().set('document_type_key', documentTypeKey);
    if (branchId) params = params.set('branch_id', branchId);
    if (stepCount !== undefined) params = params.set('step_count', stepCount);
    return this.api.get<BusinessDocumentPipelineBundle[]>(BUSINESS_DOCUMENT_GET_PIPELINES_ENDPOINT, { params });
  }

  pipeline(id: string) {
    return this.api.get<BusinessDocumentPipelineBundle>(
      formatString(BUSINESS_DOCUMENT_PIPELINE_ENDPOINT, id),
    );
  }

  createPipeline(input: CreatePipelineInput) {
    return this.api.post<BusinessDocumentPipelineBundle>(
      BUSINESS_DOCUMENT_CREATE_PIPELINE_ENDPOINT,
      input,
    );
  }

  updatePipeline(id: string, input: ConfigurePipelineInput) {
    return this.api.put<BusinessDocumentPipelineBundle>(
      formatString(BUSINESS_DOCUMENT_PIPELINE_ENDPOINT, id),
      input,
    );
  }

  duplicatePipeline(id: string, name: string) {
    return this.api.post<BusinessDocumentPipelineBundle>(
      formatString(BUSINESS_DOCUMENT_POST_PIPELINE_DUPLICATE_ENDPOINTS, id),
      { name },
    );
  }

  makeDefaultPipeline(id: string) {
    return this.api.post<BusinessDocumentPipelineBundle>(
      formatString(BUSINESS_DOCUMENT_POST_PIPELINE_MAKE_DEFAULT_ENDPOINT, id),
      {},
    );
  }

  archivePipeline(id: string) {
    return this.api.delete<BusinessDocumentPipeline>(
      formatString(BUSINESS_DOCUMENT_DELETE_PIPELINE_ENDPOINT, id),
    );
  }

  contributorOptions(subjectType: string, query = '') {
    const params = new HttpParams().set('query', query).set('limit', 50);
    return this.api
      .get<ContributorOption[] | { records: ContributorOption[] }>(
        formatString(BUSINESS_DOCUMENT_ENDPOINTS.contributorOptions, subjectType),
        { params },
      )
      .pipe(map((response) => (Array.isArray(response) ? response : response.records)));
  }

  workspace(id: string) {
    return this.api.get<BusinessDocumentWorkspaceModel>(
      formatString(BUSINESS_DOCUMENT_ENDPOINTS.workspace, id),
    ).pipe(
      map((workspace) => ({
        ...workspace,
        verification_requests: workspace.verification_requests.map((request) => {
          const status = String(request['status'] ?? '').toLowerCase();
          return {
            ...request,
            received_approvals: request['received_approvals'] ?? request['approval_count'] ?? 0,
            has_decided: request['has_decided'] ?? status !== 'pending',
          };
        }),
      })),
    );
  }

  access(id: string) {
    return this.api.get<BusinessDocumentStepAccess>(
      formatString(BUSINESS_DOCUMENT_ENDPOINTS.access, id),
    );
  }

  transition(
    id: string,
    actionCode: string,
    contributorRoleCode: string | null,
    viewMode: 'observe' | 'edit' = 'edit',
  ) {
    return this.api.post(
      formatString(BUSINESS_DOCUMENT_ENDPOINTS.transition, id),
      {
        action_code: actionCode,
        contributor_role_code: contributorRoleCode,
        view_mode: viewMode,
        record_snapshot: {},
      },
    );
  }

  outStepLink(id: string, regenerate = false) {
    const params = new HttpParams().set('regenerate', String(regenerate));
    return this.api.get<OutStepLink>(
      formatString(BUSINESS_DOCUMENT_ENDPOINTS.outStepLink, id),
      { params },
    );
  }

  publicOutStep(token: string) {
    return this.api.get<PublicOutStep>(
      formatString(BUSINESS_DOCUMENT_ENDPOINTS.publicOutStep, encodeURIComponent(token)),
    );
  }

  respondToOutStep(token: string, actionCode: string) {
    return this.api.post<PublicOutStep>(
      formatString(BUSINESS_DOCUMENT_ENDPOINTS.publicOutStep, encodeURIComponent(token)),
      { action_code: actionCode },
    );
  }

  verificationRequests() {
    return this.api.get<VerificationRequest[]>(BUSINESS_DOCUMENT_ENDPOINTS.verificationRequests);
  }

  decideVerification(id: string, decision: 'approved' | 'changes_requested' | 'rejected', note: string) {
    return this.api.post(
      formatString(BUSINESS_DOCUMENT_ENDPOINTS.verificationDecision, id),
      { decision, note: note.trim() || null },
    );
  }

  notes(documentId: string) {
    return this.api.get<Array<Record<string, unknown>>>(
      formatString(BUSINESS_DOCUMENT_ENDPOINTS.verificationNotes, documentId),
    );
  }

  addNote(documentId: string, body: string, requestId: string | null, parentNoteId: string | null) {
    return this.api.post(
      formatString(BUSINESS_DOCUMENT_ENDPOINTS.verificationNotes, documentId),
      {
        request_id: requestId,
        parent_note_id: parentNoteId,
        body,
      },
    );
  }
}
